/**
 * Les ordres de transport, vus par l'écran — le calcul, hors de React.
 *
 * Même découpe que `parc.ts` et `affectation.ts`, et pour la même raison : ce
 * qui est ici se teste sans navigateur, sans base et sans session.
 *
 * TROIS CHOSES QUE CE FICHIER SAIT, ET QUE L'ÉCRAN N'A PAS À REFAIRE :
 *
 *   - **un ordre n'a ni date ni trajet à lui.** Les deux viennent des étapes, et
 *     l'API les rend déjà calculés (`jour_debut`, `ville_depart`…). Ici on les
 *     met en français ;
 *   - **les totaux de marchandise se somment sur les CHARGEMENTS seuls.**
 *     Compter aussi les livraisons compterait deux fois la même palette ;
 *   - **un instant se compose et se décompose.** L'API exige un fuseau sur
 *     `fait_le` et sur les dates de litige — décidé le 04/09/2026 après le
 *     défaut où une livraison de 14 h 30 se serait enregistrée à 16 h 30.
 *     L'écran, lui, montre deux champs : un jour et une heure.
 */

import { metresLineaires, mlLisible } from '@stafa/core';

/** Un ordre de transport, tel que l'API le rend. */
export interface OrdreTransport {
  readonly id: string;
  readonly numero: string;
  readonly client_id: string;
  readonly client: string;
  readonly reference_client: string | null;
  readonly gabarit_code: string | null;
  readonly gabarit: string | null;
  readonly marchandise: string | null;
  /** `numeric` rendu en chaîne : jamais converti en flottant. */
  readonly prix_vente: string | null;
  readonly distance_km: number | null;
  readonly statut: string;
  readonly note: string | null;
  /** Calculés des étapes par l'API. `null` tant qu'il n'y a pas d'étape. */
  readonly jour_debut: string | null;
  readonly jour_fin: string | null;
  readonly ville_depart: string | null;
  readonly ville_arrivee: string | null;
  /**
   * Le premier CHARGEMENT et la dernière LIVRAISON par rang — à ne pas
   * confondre avec les quatre champs ci-dessus, qui donnent le premier et le
   * dernier arrêt quel que soit leur type. Sur un relais ou une ramasse, ce
   * n'est pas la même étape.
   *
   * `null` quand l'ordre n'a pas d'étape de ce type.
   */
  readonly chargement_jour: string | null;
  readonly chargement_heure: string | null;
  readonly chargement_site: string | null;
  readonly chargement_code_postal: string | null;
  readonly chargement_ville: string | null;
  readonly livraison_jour: string | null;
  readonly livraison_heure: string | null;
  readonly livraison_site: string | null;
  readonly livraison_code_postal: string | null;
  readonly livraison_ville: string | null;
  /**
   * L'AFFRÈTEMENT EN COURS, s'il y en a un. `null` sur un ordre fait en propre.
   *
   * C'est ce qui distingue « Affrété · DUMONT » de « En propre » dans la
   * colonne Traité par — la colonne qui a remplacé le module Affrètement le
   * 05/09/2026.
   *
   * `prix_achat` est ici, `prix_vente` plus haut : la marge se calcule des
   * deux et n'est stockée nulle part.
   */
  /**
   * L'AXE DÉDUIT, au format des zones : « FR45 → ES28 ». Calculé par l'API, qui
   * traduit le pays avec la table `pays` — l'écran ne le recalcule pas.
   *
   * `null` quand le pays manque sur l'étape : on ne devine pas.
   */
  readonly zone_depart: string | null;
  readonly zone_arrivee: string | null;
  readonly affretement_id: string | null;
  readonly affrete_par: string | null;
  readonly prix_achat: string | null;
  readonly etapes: number;
  readonly litiges_ouverts: number;
  readonly cree_le: string;
  readonly modifie_le: string;
}

export type TypeEtape = 'chargement' | 'livraison';

export interface Etape {
  readonly id: string;
  readonly ot_id: string;
  readonly rang: number;
  readonly type: string;
  readonly site_id: string;
  /** L'adresse FIGÉE du jour, recopiée du site à la création. */
  readonly site_nom: string;
  readonly site_adresse: string | null;
  readonly site_code_postal: string | null;
  readonly site_ville: string | null;
  /**
   * Le pays, figé comme les quatre lignes ci-dessus. `null` sur les étapes
   * saisies avant le 06/09/2026 : on ne devine pas un pays, et « 28906 » ne dit
   * pas l'Espagne. C'est ce champ qui permet de déduire l'axe international
   * d'un affrètement.
   */
  readonly site_pays: string | null;
  readonly jour: string;
  readonly heure_debut: string | null;
  readonly heure_fin: string | null;
  readonly quantite: string | null;
  readonly support: string | null;
  readonly poids: number | null;
  /**
   * LE CAMION DE CETTE ÉTAPE — sur l'étape et non sur l'ordre, décidé par
   * l'utilisateur le 04/09/2026 : « le relais doit être possible ». Dans la
   * base v1, 3 missions sur 94 portent deux tracteurs différents.
   */
  readonly vehicule_id: string | null;
  readonly vehicule: string | null;
  /**
   * Le chauffeur et la remorque de CETTE étape, FIGÉS.
   *
   * Recopiés de l'attelage du jour au moment où l'on pose le camion, puis plus
   * jamais relus — décidé par l'utilisateur le 04/09/2026 : « changer un
   * attelage ne doit pas modifier les OT du passé ». Même règle que les quatre
   * lignes d'adresse du site, juste au-dessus.
   */
  readonly chauffeur_id: string | null;
  readonly chauffeur: string | null;
  readonly remorque_id: string | null;
  readonly remorque: string | null;
  readonly fait: boolean;
  readonly fait_le: string | null;
  /**
   * L'ÉCHANGE DE PALETTES DE CETTE ÉTAPE, s'il y en a un.
   *
   * Rendu par la fiche de l'ordre, mais écrit par le module Emballage
   * (`PUT /emballage/etapes/:id`) : le cadrage §6 veut la saisie sur l'étape,
   * et le calcul du solde dans son module. On le montre ici pour pouvoir le
   * corriger, pas pour le recalculer.
   */
  readonly echange_support: string | null;
  readonly echange_livrees: number | null;
  readonly echange_reprises: number | null;
  readonly echange_bon_id: string | null;
  readonly echange_bon_numero: string | null;
  /**
   * LE LITIGE VIT DANS SA TABLE depuis le 07/09/2026. La fiche montre LE PLUS
   * RÉCENT de l'étape, résolu ou non, sous les mêmes noms de champs qu'avant —
   * seule leur source a changé. `litige_id` est nouveau : on résout CE
   * litige-là, et non « le litige de cette étape », qui n'est plus unique.
   */
  readonly litige_id: string | null;
  readonly motif_litige_id: string | null;
  readonly motif_litige: string | null;
  readonly litige_note: string | null;
  readonly litige_ouvert_le: string | null;
  readonly litige_resolu_le: string | null;
  readonly litige_resolution: string | null;
  readonly note: string | null;
}

/** L'ordre AVEC ses étapes : l'API ne rend jamais l'un sans les autres. */
export interface FicheOrdre {
  readonly ordre_transport: OrdreTransport;
  readonly etapes: readonly Etape[];
}

/** Un motif de litige, propre à la société. */
export interface MotifLitige {
  readonly id: string;
  readonly nom: string;
  readonly actif: boolean;
  /**
   * Le nombre d'étapes qui le portent, calculé par l'API. Ajouté le 04/09/2026
   * avec l'écran des Paramètres, qui l'affiche et s'en sert pour savoir ce qui
   * peut encore se supprimer. Facultatif : les écrans qui ne l'emploient pas
   * n'ont pas à le connaître.
   */
  readonly emploi?: number;
}

// ---------------------------------------------------------------------------
// Les statuts
// ---------------------------------------------------------------------------

/**
 * Les cinq statuts, dans l'ordre du cycle de vie.
 *
 * La liste est FIGÉE, ici comme dans l'API et comme en base — c'est la seule du
 * dépôt à l'être, et l'exception est expliquée dans
 * `apps/api/src/ordres_transport.ts` : le logiciel raisonne dessus.
 *
 * `classe` nomme la couleur, elle ne la contient pas : les valeurs sont dans la
 * feuille de style, et une copie ici finirait par diverger.
 *
 * Cette liste sert à MONTRER un statut et à FILTRER la liste. Elle ne sert plus
 * à en choisir un depuis le 04/09/2026 : les quatre premiers se déduisent des
 * étapes, et « Annulé » se pose par le bouton de la fiche.
 */
export const STATUTS: readonly {
  readonly cle: string;
  readonly nom: string;
  readonly classe: string;
}[] = [
  { cle: 'attente', nom: 'Attente', classe: 'attente' },
  { cle: 'planifie', nom: 'Planifié', classe: 'planifie' },
  { cle: 'en_cours', nom: 'En cours', classe: 'encours' },
  { cle: 'livre', nom: 'Livré', classe: 'livre' },
  { cle: 'annule', nom: 'Annulé', classe: 'annule' },
];

/**
 * Le statut en français.
 *
 * Rend le code brut si le statut est inconnu, plutôt que rien : le jour où
 * l'API en ajoute un, l'écran l'affiche tel quel au lieu de laisser une case
 * vide dont personne ne comprendrait la cause.
 */
export function statutDit(statut: string): string {
  return STATUTS.find((s) => s.cle === statut)?.nom ?? statut;
}

export function classeStatut(statut: string): string {
  return STATUTS.find((s) => s.cle === statut)?.classe ?? 'attente';
}

// ---------------------------------------------------------------------------
// Les dates, les heures, les instants
// ---------------------------------------------------------------------------

/** « 2026-09-04 » → « 04/09/2026 ». `null` si la date est illisible. */
export function enFrancais(iso: string | null): string | null {
  if (iso === null) return null;
  const morceaux = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (morceaux === null) return null;
  return `${morceaux[3] as string}/${morceaux[2] as string}/${morceaux[1] as string}`;
}

/** « 2026-09-04 » → « 04/09 ». Pour les créneaux, où l'année est comprise. */
export function jourCourt(iso: string | null): string | null {
  if (iso === null) return null;
  const morceaux = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (morceaux === null) return null;
  return `${morceaux[3] as string}/${morceaux[2] as string}`;
}

/**
 * « 08:00:00 » → « 08:00 ».
 *
 * L'API rend toujours les secondes, parce que c'est ce que PostgreSQL écrit et
 * que « 08:00 » et « 08:00:00 » se compareraient autrement. Personne ne veut
 * les lire sur un créneau de quai.
 */
export function heureCourte(heure: string | null): string | null {
  if (heure === null) return null;
  const morceaux = /^(\d{2}):(\d{2})/.exec(heure);
  if (morceaux === null) return null;
  return `${morceaux[1] as string}:${morceaux[2] as string}`;
}

/** « 08:00 → 09:30 », « à partir de 08:00 », « jusqu'à 09:30 », ou `null`. */
export function creneau(etape: {
  readonly heure_debut: string | null;
  readonly heure_fin: string | null;
}): string | null {
  const debut = heureCourte(etape.heure_debut);
  const fin = heureCourte(etape.heure_fin);
  if (debut !== null && fin !== null) return `${debut} → ${fin}`;
  if (debut !== null) return `à partir de ${debut}`;
  if (fin !== null) return `jusqu’à ${fin}`;
  return null;
}

/**
 * Le jour d'aujourd'hui en `AAAA-MM-JJ`, DANS LE FUSEAU DE LA PERSONNE.
 *
 * Pas `toISOString()`, qui rendrait le jour à Greenwich : le 1er janvier à
 * 00 h 30 en France, il donnerait le 31 décembre. Même fonction que dans
 * `affectation.ts` ; elle y est répétée plutôt qu'importée pour ne pas faire
 * dépendre l'écran des ordres de celui des affectations, qui n'ont rien à voir.
 */
export function aujourdhui(maintenant: Date = new Date()): string {
  const mois = String(maintenant.getMonth() + 1).padStart(2, '0');
  const jour = String(maintenant.getDate()).padStart(2, '0');
  return `${maintenant.getFullYear()}-${mois}-${jour}`;
}

/** Le premier et le dernier jour du mois qui contient `jour`. */
export function moisDe(jour: string): { readonly du: string; readonly au: string } {
  const morceaux = /^(\d{4})-(\d{2})-\d{2}$/.exec(jour);
  if (morceaux === null) return { du: jour, au: jour };
  const annee = Number(morceaux[1]);
  const mois = Number(morceaux[2]);
  // Le jour 0 du mois suivant est le dernier du mois courant — y compris en
  // février d'une année bissextile, qu'on n'a donc pas à savoir compter.
  const dernier = new Date(Date.UTC(annee, mois, 0)).getUTCDate();
  const deux = (n: number) => String(n).padStart(2, '0');
  return {
    du: `${morceaux[1] as string}-${morceaux[2] as string}-01`,
    au: `${morceaux[1] as string}-${morceaux[2] as string}-${deux(dernier)}`,
  };
}

/**
 * Compose l'INSTANT que l'API attend, à partir des deux champs de l'écran.
 *
 * L'API exige un fuseau — `2026-09-10T14:30` sans fuseau serait compris à
 * Greenwich, et une livraison faite à 14 h 30 en France s'enregistrerait à
 * 16 h 30. `toISOString()` en pose un, `Z`, et convertit correctement : le
 * constructeur `Date` interprète les composantes dans le fuseau de la machine,
 * qui est celui de la personne devant l'écran.
 *
 * Rend `null` si l'un des deux champs manque : un instant sans heure n'existe
 * pas, et poser midi d'office inventerait une donnée.
 */
export function instantDepuis(jour: string, heure: string): string | null {
  const j = /^(\d{4})-(\d{2})-(\d{2})$/.exec(jour);
  const h = /^(\d{2}):(\d{2})/.exec(heure);
  if (j === null || h === null) return null;

  const annee = Number(j[1]);
  const mois = Number(j[2]);
  const jourDuMois = Number(j[3]);
  const date = new Date(annee, mois - 1, jourDuMois, Number(h[1]), Number(h[2]));
  if (Number.isNaN(date.getTime())) return null;

  // `new Date(2026, 1, 31)` NE SE PLAINT PAS : elle rend le 3 mars. Sans cette
  // comparaison, un 31 février composerait un instant décalé de trois jours,
  // en silence. Trouvé par un test le 04/09/2026 ; c'est la même parade que
  // `dateDuJour` côté API. Le champ `<input type="date">` ne laisse pas taper
  // une telle date, mais la fonction ne doit pas dépendre de qui l'appelle.
  if (
    date.getFullYear() !== annee ||
    date.getMonth() !== mois - 1 ||
    date.getDate() !== jourDuMois
  ) {
    return null;
  }

  return date.toISOString();
}

/** Le jour d'un instant, en `AAAA-MM-JJ`, dans le fuseau de la personne. */
export function jourDe(instant: string | null): string {
  if (instant === null) return '';
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return '';
  return aujourdhui(date);
}

/** L'heure d'un instant, en `HH:MM`, dans le fuseau de la personne. */
export function heureDe(instant: string | null): string {
  if (instant === null) return '';
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return '';
  const deux = (n: number) => String(n).padStart(2, '0');
  return `${deux(date.getHours())}:${deux(date.getMinutes())}`;
}

/** « 03/09/2026 à 08:42 » — un instant lu par un humain. */
export function instantDit(instant: string | null): string | null {
  if (instant === null) return null;
  const jour = enFrancais(jourDe(instant));
  const heure = heureDe(instant);
  if (jour === null || heure === '') return null;
  return `${jour} à ${heure}`;
}

// ---------------------------------------------------------------------------
// Ce qu'on lit d'un ordre
// ---------------------------------------------------------------------------

/**
 * Les dates de l'ordre : la première, et la seconde SEULEMENT si elle diffère.
 *
 * La deuxième ligne du tableau n'apparaît que si le transport passe la nuit —
 * demandé sur la maquette. Répéter la même date deux fois ferait croire à un
 * ordre sur deux jours.
 */
export function datesDites(ordre: {
  readonly jour_debut: string | null;
  readonly jour_fin: string | null;
}): { readonly premiere: string | null; readonly seconde: string | null } {
  const premiere = enFrancais(ordre.jour_debut);
  const fin = enFrancais(ordre.jour_fin);
  return {
    premiere,
    seconde: fin !== null && fin !== premiere ? fin : null,
  };
}

/** « Rouen » et « → Amiens ». `null` de part et d'autre s'il n'y a pas d'étape. */
export function trajetDit(ordre: {
  readonly ville_depart: string | null;
  readonly ville_arrivee: string | null;
}): { readonly depart: string | null; readonly arrivee: string | null } {
  const depart = ordre.ville_depart;
  const arrivee = ordre.ville_arrivee;
  return {
    depart,
    // Un aller-retour sur la même ville n'a pas à s'écrire deux fois.
    arrivee: arrivee !== null && arrivee !== depart ? arrivee : null,
  };
}

/**
 * Un bout de l'ordre, tel que le tableau l'affiche : une date, une heure, un
 * site, une localité.
 *
 * Quatre champs séparés et non une phrase toute faite : la maquette du
 * 05/09/2026 met la date et l'heure dans une colonne, le site et la localité
 * dans la suivante, l'heure et la localité en second rang gris. Les recoller
 * ici obligerait l'écran à les redécouper.
 */
export interface BoutDOrdre {
  readonly jour: string | null;
  readonly heure: string | null;
  readonly site: string | null;
  /** « 45770 Saran ». `null` si ni code postal ni ville ne sont renseignés. */
  readonly localite: string | null;
}

function bout(
  jour: string | null,
  heure: string | null,
  site: string | null,
  codePostal: string | null,
  ville: string | null,
): BoutDOrdre {
  const localite = [codePostal, ville]
    .filter((m): m is string => m !== null && m.trim() !== '')
    .join(' ');

  return {
    jour: enFrancais(jour),
    heure: heureCourte(heure),
    site: site !== null && site.trim() !== '' ? site : null,
    localite: localite !== '' ? localite : null,
  };
}

/**
 * Le premier chargement de l'ordre — colonnes « Chargement » et « Adresse de
 * chargement » du tableau.
 *
 * Tout à `null` sur un ordre qui n'a pas encore d'étape de chargement. Le
 * tableau affiche alors le tiret d'absence, il ne cache pas la ligne.
 */
export function chargementDit(ordre: {
  readonly chargement_jour: string | null;
  readonly chargement_heure: string | null;
  readonly chargement_site: string | null;
  readonly chargement_code_postal: string | null;
  readonly chargement_ville: string | null;
}): BoutDOrdre {
  return bout(
    ordre.chargement_jour,
    ordre.chargement_heure,
    ordre.chargement_site,
    ordre.chargement_code_postal,
    ordre.chargement_ville,
  );
}

/** La dernière livraison de l'ordre. Même règle que `chargementDit`. */
export function livraisonDit(ordre: {
  readonly livraison_jour: string | null;
  readonly livraison_heure: string | null;
  readonly livraison_site: string | null;
  readonly livraison_code_postal: string | null;
  readonly livraison_ville: string | null;
}): BoutDOrdre {
  return bout(
    ordre.livraison_jour,
    ordre.livraison_heure,
    ordre.livraison_site,
    ordre.livraison_code_postal,
    ordre.livraison_ville,
  );
}

/** Les quatre lignes d'adresse d'une étape, celles qui sont renseignées. */
export function adresseDite(etape: Etape): readonly string[] {
  const codePostalEtVille = [etape.site_code_postal, etape.site_ville]
    .filter((m): m is string => m !== null && m.trim() !== '')
    .join(' ');

  return [etape.site_nom, etape.site_adresse ?? '', codePostalEtVille].filter(
    (ligne) => ligne.trim() !== '',
  );
}

/**
 * L'heure de MAINTENANT, en `HH:MM`, dans le fuseau de la personne.
 *
 * Sert à pré-remplir la boîte de validation : on valide presque toujours une
 * étape au moment où elle vient d'être faite, et retaper l'heure serait un
 * geste de plus à chaque étape.
 */
export function maintenant(horloge: Date = new Date()): string {
  const deux = (n: number) => String(n).padStart(2, '0');
  return `${deux(horloge.getHours())}:${deux(horloge.getMinutes())}`;
}

/**
 * Ce qui roule sur cette étape, en une phrase : « GZ-083-NF · Patrick Perrin ».
 *
 * `null` tant qu'aucun camion n'est posé — l'étape n'est pas encore planifiée,
 * et c'est le cas ordinaire à la saisie. Le chauffeur et la remorque ne sont
 * ajoutés que s'ils existent : un camion sans attelage ce jour-là se dit
 * quand même, il ne s'invente pas de conducteur.
 */
export function ressourceDite(etape: {
  readonly vehicule: string | null;
  readonly chauffeur: string | null;
  readonly remorque: string | null;
}): string | null {
  if (etape.vehicule === null) return null;
  const suite = [etape.remorque, etape.chauffeur].filter(
    (m): m is string => m !== null && m.trim() !== '',
  );
  return suite.length === 0
    ? etape.vehicule
    : `${etape.vehicule} · ${suite.join(' · ')}`;
}

/** « Ouvert le 03/09/2026 à 08:42 · non résolu », ou « … · résolu le … ». */
export function litigeDit(etape: Etape): string | null {
  if (etape.motif_litige_id === null) return null;
  const ouvert = instantDit(etape.litige_ouvert_le);
  const resolu = instantDit(etape.litige_resolu_le);
  const debut = ouvert === null ? 'Ouvert' : `Ouvert le ${ouvert}`;
  return resolu === null ? `${debut} · non résolu` : `${debut} · résolu le ${resolu}`;
}

/**
 * Les totaux de marchandise, SUR LES CHARGEMENTS SEULS.
 *
 * Sommer aussi les livraisons compterait deux fois la même palette : elle est
 * chargée puis livrée. Le support n'est rendu que si tous les chargements
 * portent le même — sinon « 24 Palette 80×120 » mentirait sur la moitié du
 * chargement.
 */
export function totaux(etapes: readonly Etape[]): {
  readonly quantite: number | null;
  readonly support: string | null;
  readonly poids: number | null;
} {
  const chargements = etapes.filter((e) => e.type === 'chargement');

  const quantites = chargements
    .map((e) => (e.quantite === null ? null : Number(e.quantite)))
    .filter((n): n is number => n !== null && Number.isFinite(n));
  const poids = chargements
    .map((e) => e.poids)
    .filter((n): n is number => n !== null && Number.isFinite(n));

  const supports = new Set(
    chargements
      .map((e) => e.support)
      .filter((s): s is string => s !== null && s.trim() !== ''),
  );

  return {
    quantite: quantites.length === 0 ? null : quantites.reduce((a, b) => a + b, 0),
    support: supports.size === 1 ? ([...supports][0] as string) : null,
    poids: poids.length === 0 ? null : poids.reduce((a, b) => a + b, 0),
  };
}

/**
 * Un rapport du prix à une quantité — à la palette, au kilomètre.
 *
 * Rend une CHAÎNE à deux décimales, jamais un nombre : elle est destinée à être
 * affichée, et l'appelant n'a pas à choisir un arrondi. `null` dès que le
 * calcul n'a pas de sens — pas de prix, pas de quantité, division par zéro.
 */
export function rapport(prix: string | null, quantite: number | null): string | null {
  if (prix === null || quantite === null || quantite === 0) return null;
  const montant = Number(prix);
  if (!Number.isFinite(montant)) return null;
  return (montant / quantite).toFixed(2).replace('.', ',');
}

/** « 12 400 » — un nombre lu par un humain, espaces de milliers compris. */
export function lisible(nombre: number | null): string | null {
  if (nombre === null || !Number.isFinite(nombre)) return null;
  return new Intl.NumberFormat('fr-FR').format(nombre);
}

/** « 610,00 € ». L'API rend un `numeric` en chaîne — jamais un flottant. */
export function enEuros(montant: string | null): string | null {
  if (montant === null || montant.trim() === '') return null;
  const nombre = Number(montant);
  if (!Number.isFinite(nombre)) return montant;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(nombre);
}

// ---------------------------------------------------------------------------
// La liste
// ---------------------------------------------------------------------------

/** Sans accent ni casse : « Alizay » se trouve en tapant « alizay ». */
function aplatir(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * Filtre la liste DÉJÀ CHARGÉE, sur tout ce qui se voit à l'écran.
 *
 * Le filtrage est local, comme dans les annuaires : la fenêtre de dates borne
 * déjà ce que l'API rend, et rappeler l'API à chaque frappe ferait clignoter la
 * liste pour un calcul que le navigateur fait en une milliseconde.
 */
export function filtrer(
  ordres: readonly OrdreTransport[],
  recherche: string,
): readonly OrdreTransport[] {
  const terme = aplatir(recherche.trim());
  if (terme === '') return ordres;

  return ordres.filter((o) =>
    [
      o.numero,
      o.client,
      o.reference_client ?? '',
      o.ville_depart ?? '',
      o.ville_arrivee ?? '',
      // Les lieux du tableau : chercher « Lesquin » ne trouverait rien alors
      // que le mot est à l'écran. Les villes de départ et d'arrivée ne les
      // couvrent pas — ce ne sont pas les mêmes étapes sur un relais.
      o.chargement_site ?? '',
      o.chargement_code_postal ?? '',
      o.chargement_ville ?? '',
      o.livraison_site ?? '',
      o.livraison_code_postal ?? '',
      o.livraison_ville ?? '',
      o.gabarit ?? '',
      o.marchandise ?? '',
    ].some((champ) => aplatir(champ).includes(terme)),
  );
}

/** Du plus récent au plus ancien : le numéro croît avec le temps. */
export function ranger(ordres: readonly OrdreTransport[]): readonly OrdreTransport[] {
  return [...ordres].sort((a, b) => b.numero.localeCompare(a.numero, 'fr'));
}

/** « 94 ordres de transport — dont 4 en cours, 12 planifiés ». */
export function decompte(ordres: readonly OrdreTransport[]): string {
  const total = ordres.length;
  const tete =
    total > 1 ? `${total} ordres de transport` : `${total} ordre de transport`;

  const enCours = ordres.filter((o) => o.statut === 'en_cours').length;
  const planifies = ordres.filter((o) => o.statut === 'planifie').length;
  const litiges = ordres.filter((o) => o.litiges_ouverts > 0).length;

  const details: string[] = [];
  if (enCours > 0) details.push(`${enCours} en cours`);
  if (planifies > 0)
    details.push(planifies > 1 ? `${planifies} planifiés` : '1 planifié');
  if (litiges > 0) {
    details.push(litiges > 1 ? `${litiges} avec litige` : '1 avec litige');
  }

  return details.length === 0 ? tete : `${tete} — dont ${details.join(', ')}`;
}

// ---------------------------------------------------------------------------
// Les supports
// ---------------------------------------------------------------------------

/**
 * Les supports de charge, LISTE FERMÉE À L'ÉCRAN — la base, elle, reste
 * ouverte.
 *
 * Demandé par l'utilisateur le 04/09/2026 : « les support via liste
 * déroulable ». **Cela contredit un commentaire de la migration
 * `20260904090000`**, qui écrivait : « `support` est un texte : la liste fermée
 * viendra avec le module Paramètres, et la figer ici imposerait les emballages
 * de TNS à tous les clients de Stafa ». La contradiction a été signalée avant
 * d'écrire, et la voie retenue est celle DÉJÀ EN USAGE dans ce dépôt pour les
 * pays et les conditions de paiement : la règle n'existe que dans l'interface,
 * la colonne reste un `text` sans contrainte. Le jour où le module Paramètres
 * existera, cette constante deviendra une table et rien d'autre ne bougera.
 *
 * POURQUOI FERMER : dans la base v1, le support d'une étape s'écrit
 * `Palette 80×120` 150 fois, `Palette(s)` 14 fois, `MPL` 14 fois et `Mlp`
 * 2 fois — **quatre orthographes pour deux choses**. Rien ne se compte sur une
 * colonne écrite ainsi.
 *
 * CE QUE PORTE CETTE LISTE, et d'où ça vient — l'utilisateur a répondu le
 * 04/09/2026 : « les formats de palettes les plus utilisés et le ML » :
 *
 *   - `Palette 80×120` : CONSTATÉE, 150 fois dans la base v1 ;
 *   - `Palette 100×120` et `Demi-palette 60×80` : les deux autres formats
 *     courants du transport routier français. **Non constatés dans la base
 *     v1** — proposés, et à corriger si l'un ne sert pas ;
 *   - `ML`, le mètre linéaire. Écrit `MPL` 14 fois et `Mlp` 2 fois dans la
 *     base v1 ; l'utilisateur dit `ML`, c'est donc `ML`. Les étapes anciennes
 *     qui portent l'ancienne orthographe la gardent : `supportsAvec` la remet
 *     en tête de liste plutôt que de la faire disparaître.
 *
 * **CE QUI N'EST PAS FAIT** : l'utilisateur précise qu'un ML « ne peut jamais
 * dépasser la limite du camion ». Aucun contrôle ne le vérifie, et c'est
 * délibéré — la longueur de plancher d'un gabarit n'est nulle part en base, et
 * poser 13,6 m en dur imposerait la semi de TNS à tous les clients de Stafa.
 * Le contrôle viendra quand le référentiel des gabarits portera une longueur.
 */
/**
 * La liste à montrer, la valeur enregistrée COMPRISE même si elle n'y est plus.
 *
 * Les supports VIENNENT DE LA SOCIÉTÉ depuis le 04/09/2026 (migration
 * 20260904170000) : la liste de quatre valeurs qui vivait ici était celle de
 * TNS, servie à tous les clients de Stafa. Elle a été reprise en base, où
 * chacun la modifie.
 *
 * Ce qui reste ici est la seule règle d'écran : garder la valeur enregistrée
 * en tête de liste quand elle n'y figure plus. Sans cela, ouvrir une étape
 * ancienne afficherait autre chose que ce qui est enregistré, et un simple
 * aller-retour changerait le support sans que personne l'ait demandé.
 *
 * **CE QUI N'EST TOUJOURS PAS FAIT** : l'utilisateur précise qu'un ML « ne peut
 * jamais dépasser la limite du camion ». Aucun contrôle ne le vérifie — la
 * longueur de plancher d'un gabarit n'est nulle part en base, et poser 13,6 m
 * en dur imposerait la semi de TNS à tous les clients de Stafa.
 */
export function supportsAvec(
  valeurActuelle: string,
  supports: readonly string[],
): readonly string[] {
  const valeur = valeurActuelle.trim();
  if (valeur === '' || supports.includes(valeur)) return supports;
  return [valeur, ...supports];
}

/**
 * La marchandise d'un chargement se REPORTE sur les livraisons qui n'ont rien.
 *
 * Demandé par l'utilisateur le 04/09/2026 : « en tapant sur la case chargement
 * les quantité et support se transposent à la livraison pour faciliter la
 * saisie ». Dans la très grande majorité des transports, ce qui est chargé est
 * ce qui est livré — 87 des 94 missions de la base v1 n'ont qu'un chargement et
 * une livraison.
 *
 * **CE QUI EST DÉJÀ SAISI N'EST JAMAIS ÉCRASÉ.** Une livraison partielle, où
 * l'on dépose 12 des 24 palettes, garde ses 12 : le report est une aide à la
 * saisie, pas une règle de gestion.
 *
 * LE POIDS EST REPORTÉ LUI AUSSI, décidé par l'utilisateur le 04/09/2026 après
 * la question — il n'avait d'abord nommé que la quantité et le support.
 *
 * Rend la liste ENTIÈRE, chargements compris, pour que l'appelant la pose telle
 * quelle.
 */
export function reporterSurLesLivraisons(
  etapes: readonly SaisieEtape[],
  rangModifie: number,
): readonly SaisieEtape[] {
  const source = etapes[rangModifie];
  if (source === undefined || source.type !== 'chargement') return etapes;
  if (
    source.quantite.trim() === '' &&
    source.support.trim() === '' &&
    source.poids.trim() === ''
  ) {
    return etapes;
  }

  return etapes.map((etape, rang) => {
    if (rang === rangModifie || etape.type !== 'livraison') return etape;
    return {
      ...etape,
      quantite: etape.quantite.trim() === '' ? source.quantite : etape.quantite,
      support: etape.support.trim() === '' ? source.support : etape.support,
      poids: etape.poids.trim() === '' ? source.poids : etape.poids,
    };
  });
}

// ---------------------------------------------------------------------------
// La saisie d'un ordre
// ---------------------------------------------------------------------------

/**
 * PAS DE `statut` ICI, ET C'EST LE POINT. Il se déduit des étapes ; le poser
 * dans ce formulaire donnerait une valeur que la première étape validée
 * réécrirait sans prévenir. Annuler et rouvrir sont les deux seules décisions
 * qui restent, et elles se prennent par les boutons de la fiche.
 */
export interface SaisieOrdre {
  readonly client_id: string;
  readonly reference_client: string;
  readonly gabarit_code: string;
  readonly marchandise: string;
  readonly prix_vente: string;
  readonly distance_km: string;
  readonly note: string;
}

export const ORDRE_VIDE: SaisieOrdre = {
  client_id: '',
  reference_client: '',
  gabarit_code: '',
  marchandise: '',
  prix_vente: '',
  distance_km: '',
  note: '',
};

export function ordreDepuis(ordre: OrdreTransport): SaisieOrdre {
  return {
    client_id: ordre.client_id,
    reference_client: ordre.reference_client ?? '',
    gabarit_code: ordre.gabarit_code ?? '',
    marchandise: ordre.marchandise ?? '',
    prix_vente: ordre.prix_vente ?? '',
    distance_km: ordre.distance_km === null ? '' : String(ordre.distance_km),
    note: ordre.note ?? '',
  };
}

/**
 * Contrôle la saisie d'un ordre AVANT de l'envoyer.
 *
 * Il ANTICIPE le contrôle de l'API, il ne le remplace pas : la faute s'affiche
 * sous le champ concerné plutôt que dans un bandeau. Si les deux divergent,
 * c'est l'API qui a raison.
 */
export function validerOrdre(saisie: SaisieOrdre): Readonly<Record<string, string>> {
  const fautes: Record<string, string> = {};

  if (saisie.client_id.trim() === '') {
    fautes['client_id'] = 'Choisissez un client.';
  }

  const prix = saisie.prix_vente.trim().replace(',', '.');
  if (prix !== '' && !/^[0-9]{1,10}(\.[0-9]{1,2})?$/.test(prix)) {
    fautes['prix_vente'] = 'Un montant positif, deux décimales au plus.';
  }

  const km = saisie.distance_km.trim();
  if (km !== '' && !/^[0-9]{1,6}$/.test(km)) {
    fautes['distance_km'] = 'Un nombre entier de kilomètres.';
  }
  if (km !== '' && Number(km) > 100000) {
    fautes['distance_km'] = '100 000 km au plus.';
  }

  if (saisie.marchandise.trim().length > 500) {
    fautes['marchandise'] = '500 caractères au plus.';
  }
  if (saisie.reference_client.trim().length > 120) {
    fautes['reference_client'] = '120 caractères au plus.';
  }
  if (saisie.note.trim().length > 2000) {
    fautes['note'] = '2 000 caractères au plus.';
  }

  return fautes;
}

/** Ce qu'on envoie à la création : les champs vides ne partent pas. */
export function pourCreationOrdre(
  saisie: SaisieOrdre,
): Readonly<Record<string, string>> {
  const corps: Record<string, string> = { client_id: saisie.client_id.trim() };
  for (const cle of [
    'reference_client',
    'gabarit_code',
    'marchandise',
    'prix_vente',
    'distance_km',
    'note',
  ] as const) {
    const valeur = saisie[cle].trim();
    if (valeur !== '') {
      corps[cle] = cle === 'prix_vente' ? valeur.replace(',', '.') : valeur;
    }
  }
  return corps;
}

/**
 * Ce qui a changé, et rien d'autre.
 *
 * Un champ vidé part à `null` — c'est le contrat de l'API : absent = on n'y
 * touche pas, `null` = on efface. C'est aussi ce qui garde le journal des
 * traces lisible.
 */
export function differenceOrdre(
  initiale: SaisieOrdre,
  courante: SaisieOrdre,
): Readonly<Record<string, string | null>> {
  const change: Record<string, string | null> = {};
  for (const cle of Object.keys(ORDRE_VIDE) as (keyof SaisieOrdre)[]) {
    const avant = initiale[cle].trim();
    const apres = courante[cle].trim();
    if (avant === apres) continue;
    change[cle] =
      apres === '' ? null : cle === 'prix_vente' ? apres.replace(',', '.') : apres;
  }
  return change;
}

// ---------------------------------------------------------------------------
// La saisie d'une étape
// ---------------------------------------------------------------------------

export interface SaisieEtape {
  readonly type: string;
  readonly vehicule_id: string;
  readonly chauffeur_id: string;
  readonly remorque_id: string;
  readonly site_id: string;
  readonly site_nom: string;
  readonly site_adresse: string;
  readonly site_code_postal: string;
  readonly site_ville: string;
  readonly jour: string;
  readonly heure_debut: string;
  readonly heure_fin: string;
  readonly quantite: string;
  readonly support: string;
  readonly poids: string;
  readonly note: string;
  /** Coché ou non, et QUAND réellement — les deux se saisissent. */
  readonly fait: boolean;
  readonly fait_jour: string;
  readonly fait_heure: string;
  // AUCUN CHAMP DE LITIGE ICI depuis le 07/09/2026 : il a sa table et ses
  // routes, et le formulaire d'étape ne l'écrit plus.
}

export function etapeVide(type: TypeEtape, jour: string): SaisieEtape {
  return {
    type,
    vehicule_id: '',
    chauffeur_id: '',
    remorque_id: '',
    site_id: '',
    site_nom: '',
    site_adresse: '',
    site_code_postal: '',
    site_ville: '',
    jour,
    heure_debut: '',
    heure_fin: '',
    quantite: '',
    support: '',
    poids: '',
    note: '',
    fait: false,
    fait_jour: '',
    fait_heure: '',
  };
}

export function etapeDepuis(etape: Etape): SaisieEtape {
  return {
    type: etape.type,
    vehicule_id: etape.vehicule_id ?? '',
    chauffeur_id: etape.chauffeur_id ?? '',
    remorque_id: etape.remorque_id ?? '',
    site_id: etape.site_id,
    site_nom: etape.site_nom,
    site_adresse: etape.site_adresse ?? '',
    site_code_postal: etape.site_code_postal ?? '',
    site_ville: etape.site_ville ?? '',
    jour: etape.jour,
    heure_debut: heureCourte(etape.heure_debut) ?? '',
    heure_fin: heureCourte(etape.heure_fin) ?? '',
    quantite: etape.quantite ?? '',
    support: etape.support ?? '',
    poids: etape.poids === null ? '' : String(etape.poids),
    note: etape.note ?? '',
    fait: etape.fait,
    fait_jour: jourDe(etape.fait_le),
    fait_heure: heureDe(etape.fait_le),
  };
}

export function validerEtape(saisie: SaisieEtape): Readonly<Record<string, string>> {
  const fautes: Record<string, string> = {};

  if (saisie.site_id.trim() === '') {
    fautes['site_id'] = 'Choisissez un site, ou créez-le.';
  }
  if (saisie.site_nom.trim() === '') {
    fautes['site_nom'] = 'Le nom du lieu est obligatoire.';
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(saisie.jour)) {
    fautes['jour'] = 'Indiquez la date de l’étape.';
  }

  const heure = /^\d{2}:\d{2}$/;
  if (saisie.heure_debut !== '' && !heure.test(saisie.heure_debut)) {
    fautes['heure_debut'] = 'Une heure, sous la forme 08:00.';
  }
  if (saisie.heure_fin !== '' && !heure.test(saisie.heure_fin)) {
    fautes['heure_fin'] = 'Une heure, sous la forme 09:30.';
  }
  // Le créneau qui finirait avant de commencer : la base le refuse aussi, par
  // `etapes_creneau_sense`, mais par une erreur brute que personne ne lit.
  if (
    heure.test(saisie.heure_debut) &&
    heure.test(saisie.heure_fin) &&
    saisie.heure_fin < saisie.heure_debut
  ) {
    fautes['heure_fin'] = 'La fin du créneau précède son début.';
  }

  const quantite = saisie.quantite.trim().replace(',', '.');
  if (quantite !== '' && !/^[0-9]{1,8}(\.[0-9]{1,2})?$/.test(quantite)) {
    fautes['quantite'] = 'Un nombre positif, deux décimales au plus.';
  }
  const poids = saisie.poids.trim();
  if (poids !== '' && !/^[0-9]{1,6}$/.test(poids)) {
    fautes['poids'] = 'Un nombre entier de kilogrammes.';
  }
  if (poids !== '' && Number(poids) > 100000) {
    fautes['poids'] = '100 000 kg au plus.';
  }

  // Cocher « fait » sans dire quand : accepté, l'API pose l'heure de la saisie.
  // Mais une heure sans jour, ou l'inverse, ne compose aucun instant.
  if (saisie.fait_jour !== '' && saisie.fait_heure === '') {
    fautes['fait_heure'] = 'Indiquez aussi l’heure du passage.';
  }
  if (saisie.fait_heure !== '' && saisie.fait_jour === '') {
    fautes['fait_jour'] = 'Indiquez aussi le jour du passage.';
  }

  return fautes;
}

/**
 * Le corps envoyé pour une étape.
 *
 * `null` pour un champ vidé, absent pour un champ jamais renseigné : c'est la
 * même règle que partout. Les quatre lignes d'adresse partent TOUJOURS à la
 * création — l'API recopierait celles de la fiche du site, mais l'écran les a
 * peut-être modifiées.
 *
 * `fait_le`, `litige_ouvert_le` et `litige_resolu_le` sont COMPOSÉS de deux
 * champs de l'écran, un jour et une heure. Ils ne partent que si les deux sont
 * là ; sinon l'API pose l'instant de la saisie, ce qui est le comportement
 * voulu quand on coche sans préciser.
 */
export function corpsEtape(
  saisie: SaisieEtape,
  creation: boolean,
  /**
   * L'étape telle qu'elle était avant la saisie, pour les trois champs
   * d'équipage. `null` en création, ou quand l'appelant veut tout envoyer.
   */
  initiale: SaisieEtape | null = null,
): Readonly<Record<string, string | boolean | null>> {
  const corps: Record<string, string | boolean | null> = {};

  const poser = (cle: string, valeur: string) => {
    const propre = valeur.trim();
    if (creation) {
      if (propre !== '') corps[cle] = propre;
    } else {
      corps[cle] = propre === '' ? null : propre;
    }
  };

  if (creation) {
    corps['type'] = saisie.type;
  }
  corps['site_id'] = saisie.site_id.trim();
  corps['jour'] = saisie.jour;

  // L'ÉQUIPAGE est FACULTATIF et le reste : un ordre se saisit avant d'être
  // planifié. À la création on n'envoie que ce qui est choisi ; à la
  // modification, un champ vidé retire ce qu'il portait.
  //
  // Le chauffeur et la remorque ne sont envoyés QUE si l'écran en a un : les
  // laisser absents fait recopier l'attelage du jour par l'API — c'est le geste
  // ordinaire quand on pose un camion sur une étape qui n'en avait pas.
  for (const nom of ['vehicule_id', 'chauffeur_id', 'remorque_id'] as const) {
    const valeur = saisie[nom].trim();
    if (creation) {
      if (valeur !== '') corps[nom] = valeur;
      continue;
    }
    // ON N'ENVOIE QUE CE QUI A CHANGÉ, la règle du dépôt — et ici elle n'est
    // pas qu'une économie de mots. Poser un camion sur une étape qui n'avait
    // pas d'équipage n'envoie QUE le camion, et l'API recopie alors l'attelage
    // du jour. Envoyer « chauffeur : rien » l'en empêcherait, et la
    // planification ne remplirait jamais personne.
    if (initiale !== null && initiale[nom].trim() === valeur) continue;
    corps[nom] = valeur === '' ? null : valeur;
  }

  poser('site_nom', saisie.site_nom);
  poser('site_adresse', saisie.site_adresse);
  poser('site_code_postal', saisie.site_code_postal);
  poser('site_ville', saisie.site_ville);
  poser('heure_debut', saisie.heure_debut);
  poser('heure_fin', saisie.heure_fin);
  poser('quantite', saisie.quantite.replace(',', '.'));
  poser('support', saisie.support);
  poser('poids', saisie.poids);
  poser('note', saisie.note);

  if (!creation) {
    corps['fait'] = saisie.fait;
    const faitLe = instantDepuis(saisie.fait_jour, saisie.fait_heure);
    if (saisie.fait && faitLe !== null) {
      corps['fait_le'] = faitLe;
    }
  }

  return corps;
}

// ---------------------------------------------------------------------------
// Valider une étape, ouvrir un litige
// ---------------------------------------------------------------------------

/**
 * Ce qu'on saisit pour VALIDER une étape.
 *
 * « Valider », et non « c'est fait » — mot choisi par l'utilisateur le
 * 04/09/2026. Et **la date de réalisation se demande au moment où l'on
 * valide**, pas dans la fiche de l'ordre : c'est là qu'on la connaît, et la
 * chercher ailleurs obligerait à ouvrir un formulaire pour un geste qui doit
 * en être un seul.
 */
export interface SaisieValidation {
  readonly etapeId: string;
  readonly jour: string;
  readonly heure: string;
}

/** Le refus à afficher sous les champs, ou `null` si la saisie tient. */
export function refusValidation(saisie: {
  readonly jour: string;
  readonly heure: string;
}): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(saisie.jour)) {
    return 'Indiquez le jour de la réalisation.';
  }
  if (!/^\d{2}:\d{2}/.test(saisie.heure)) {
    return 'Indiquez l’heure de la réalisation.';
  }
  if (instantDepuis(saisie.jour, saisie.heure) === null) {
    return 'Cette date et cette heure n’existent pas.';
  }
  return null;
}

/**
 * Ce qu'on saisit pour ouvrir un LITIGE.
 *
 * `motifNouveau` porte le motif qu'on crée à la volée. **Sans lui, l'écran ne
 * servait à rien le premier jour** : la table `motifs_litige` naît vide — la
 * migration n'y insère aucune ligne — et aucun autre écran n'en crée. Constaté
 * par l'utilisateur le 04/09/2026 : « Litige ne marche pas ». C'est le même
 * défaut que celui évité sur les absences le 03/09, et refait ici.
 */
export interface SaisieLitige {
  readonly etapeId: string;
  readonly motifId: string;
  /** Non nul quand on est en train de créer un motif plutôt que d'en choisir un. */
  readonly motifNouveau: string | null;
  readonly note: string;
  readonly jour: string;
  readonly heure: string;
}

export function refusLitige(saisie: {
  readonly motifId: string;
  readonly motifNouveau: string | null;
  readonly jour: string;
  readonly heure: string;
}): string | null {
  if (saisie.motifNouveau !== null) {
    if (saisie.motifNouveau.trim() === '') {
      return 'Donnez un nom au motif.';
    }
    if (saisie.motifNouveau.trim().length > 120) {
      return '120 caractères au plus.';
    }
  } else if (saisie.motifId === '') {
    return 'Choisissez un motif, ou créez-en un.';
  }

  // Le constat peut n'être pas daté : l'API pose alors l'instant de la saisie.
  // Mais un jour sans heure, ou l'inverse, ne compose aucun instant.
  if (saisie.jour !== '' && saisie.heure === '') {
    return 'Indiquez aussi l’heure du constat.';
  }
  if (saisie.heure !== '' && saisie.jour === '') {
    return 'Indiquez aussi le jour du constat.';
  }
  return null;
}

// ---------------------------------------------------------------------------
// La création d'un site sans quitter l'ordre
// ---------------------------------------------------------------------------

export interface SaisieSite {
  readonly nom: string;
  readonly adresse: string;
  readonly code_postal: string;
  readonly ville: string;
  readonly code: string;
}

export const SITE_VIDE: SaisieSite = {
  nom: '',
  adresse: '',
  code_postal: '',
  ville: '',
  code: '',
};

/**
 * SEUL LE NOM EST OBLIGATOIRE — vérifié dans `apps/web/src/sites.ts`, où il est
 * le seul champ marqué `obligatoire`. Le téléphone, le courriel, la note et les
 * contacts se complètent plus tard depuis l'annuaire : les demander ici
 * retarderait la saisie de l'ordre.
 */
export function validerSite(saisie: SaisieSite): Readonly<Record<string, string>> {
  const fautes: Record<string, string> = {};
  if (saisie.nom.trim() === '') {
    fautes['nom'] = 'Le nom du site est obligatoire.';
  }
  if (saisie.nom.trim().length > 200) {
    fautes['nom'] = '200 caractères au plus.';
  }
  if (saisie.code_postal.trim().length > 16) {
    fautes['code_postal'] = '16 caractères au plus.';
  }
  return fautes;
}

export function pourCreationSite(saisie: SaisieSite): Readonly<Record<string, string>> {
  const corps: Record<string, string> = { nom: saisie.nom.trim() };
  for (const cle of ['adresse', 'code_postal', 'ville', 'code'] as const) {
    const valeur = saisie[cle].trim();
    if (valeur !== '') corps[cle] = valeur;
  }
  return corps;
}

// ---------------------------------------------------------------------------
// Le mètre linéaire, pendant qu'on saisit
// ---------------------------------------------------------------------------

/**
 * Ce que l'écran écrit sous le support pendant la saisie.
 *
 * Demandé mot pour mot par l'utilisateur le 05/09/2026 : « lorsque
 * l'utilisateur met 10 palettes 80×120, l'app doit calculer automatiquement le
 * ML correspondant et le mètre à coter (total) ». Le total DOIT se voir avant
 * d'enregistrer, sans quoi on ne saurait qu'après coup ce qu'on va facturer.
 *
 * **C'est le même calcul que celui de l'API**, `packages/core`, et non un
 * second exemplaire : ce que l'écran annonce est exactement ce qui sera figé
 * sur l'étape. Deux calculs finiraient par ne plus dire la même chose, et
 * l'écart se verrait sur une facture.
 *
 * **10 palettes valent 4,00 ML**, la convention du métier — pas les 4,80 m de
 * plancher que quatre rangs bloquent réellement. Tranché par l'utilisateur :
 * « c'est 4ml ».
 *
 * Rend `null` quand il n'y a rien à dire : pas de support, ou un support sans
 * dimensions. **On se tait plutôt que d'écrire zéro** — un « 0,00 ML » se
 * lirait « n'occupe rien », ce qui est une réponse alors qu'on n'en a aucune.
 */
export function metreLineaireDit(
  support: string,
  quantite: string,
  supports: readonly {
    readonly nom: string;
    readonly longueur_cm?: number | null;
    readonly largeur_cm?: number | null;
    readonly hauteur_cm?: number | null;
    readonly etages?: number;
  }[],
): string | null {
  if (support.trim() === '') return null;

  const trouve = supports.find(
    (s) => s.nom.toLowerCase() === support.trim().toLowerCase(),
  );
  if (trouve === undefined) return null;

  const unitaire = metresLineaires({
    nom: trouve.nom,
    abrege: null,
    longueur_cm: trouve.longueur_cm ?? null,
    largeur_cm: trouve.largeur_cm ?? null,
    hauteur_cm: trouve.hauteur_cm ?? null,
    etages: trouve.etages ?? 1,
  });
  if (unitaire === null) return null;

  // La quantité se saisit à la virgule comme au point : le champ est un texte
  // libre tant qu'il n'est pas envoyé, et l'utilisateur tape ce qu'il veut.
  const nombre = Number(quantite.trim().replace(',', '.'));
  if (quantite.trim() === '' || !Number.isFinite(nombre) || nombre <= 0) {
    return `${mlLisible(unitaire)} ML l’unité`;
  }

  return `${mlLisible(unitaire)} ML l’unité · ${mlLisible(unitaire * nombre)} ML au total`;
}


// ---------------------------------------------------------------------------
// L'échange de palettes, saisi sur l'étape
// ---------------------------------------------------------------------------

/**
 * Ce que la boîte « Échange » demande.
 *
 * DEUX CHIFFRES, ET DEUX SEULEMENT. « Restées » est livrées − reprises : le
 * saisir serait un troisième nombre à tenir d'accord, et c'est exactement ce
 * que la v1 faisait, avec `restees` ET `ecart` en plus de la quantité.
 */
export interface SaisieEchange {
  readonly support: string;
  readonly livrees: string;
  readonly reprises: string;
  readonly bonNumero: string;
  readonly bonExpire: string;
}

/**
 * L'échange pré-rempli depuis l'étape.
 *
 * « Livrées » prend la quantité de l'étape QUAND IL N'Y A PAS ENCORE
 * D'ÉCHANGE, sans y être lié : le cadrage prévoit que la quantité échangée
 * puisse différer de la quantité transportée. C'est une proposition, pas une
 * copie.
 */
export function echangeDepuis(etape: Etape): SaisieEchange {
  if (etape.echange_support !== null) {
    return {
      support: etape.echange_support,
      livrees: String(etape.echange_livrees ?? 0),
      reprises: String(etape.echange_reprises ?? 0),
      bonNumero: etape.echange_bon_numero ?? '',
      bonExpire: '',
    };
  }
  return {
    support: etape.support ?? '',
    livrees: etape.quantite === null ? '' : String(Math.round(Number(etape.quantite))),
    reprises: '',
    bonNumero: '',
    bonExpire: '',
  };
}

function entierOuRien(mot: string): number | null {
  const propre = mot.trim();
  if (propre === '') return 0;
  if (!/^\d{1,5}$/.test(propre)) return null;
  return Number(propre);
}

/**
 * Ce qui empêche d'enregistrer, en français. `null` quand tout va.
 *
 * Les mêmes refus que l'API, dits ici pour que la boîte n'ait pas besoin d'un
 * aller-retour pour les afficher. C'est le doublement habituel : l'écran
 * explique, la base garantit.
 */
export function refusEchange(saisie: SaisieEchange): string | null {
  if (saisie.support.trim() === '') {
    return 'Choisissez le support échangé.';
  }

  const livrees = entierOuRien(saisie.livrees);
  const reprises = entierOuRien(saisie.reprises);
  if (livrees === null || reprises === null) {
    return 'Les palettes se comptent en nombre entier.';
  }
  if (livrees === 0 && reprises === 0) {
    return 'Un échange sans palette livrée ni reprise n’est pas un échange.';
  }

  if (saisie.bonNumero.trim() !== '' && livrees - reprises <= 0) {
    // Un bon est une reconnaissance de dette EN NOTRE FAVEUR : il n'a pas de
    // sens quand c'est nous qui devons.
    return 'Un bon ne se justifie que si le client nous doit des palettes.';
  }

  return null;
}

/** L'écart de l'échange en cours de saisie, ou `null` s'il ne se calcule pas. */
export function ecartEchange(saisie: SaisieEchange): number | null {
  const livrees = entierOuRien(saisie.livrees);
  const reprises = entierOuRien(saisie.reprises);
  if (livrees === null || reprises === null) return null;
  return livrees - reprises;
}

/** Ce que la route `PUT /emballage/etapes/:id` attend. */
export function corpsEchange(
  saisie: SaisieEchange,
): Readonly<Record<string, string | number>> {
  const bon = saisie.bonNumero.trim();
  const expire = saisie.bonExpire.trim();
  return {
    support: saisie.support,
    livrees: entierOuRien(saisie.livrees) ?? 0,
    reprises: entierOuRien(saisie.reprises) ?? 0,
    ...(bon === '' ? {} : { bon_numero: bon }),
    ...(bon === '' || expire === '' ? {} : { bon_expire_le: expire }),
  };
}

// ---------------------------------------------------------------------------
// L'affrètement, vu de la liste
// ---------------------------------------------------------------------------

/**
 * CET ORDRE EST-IL AFFRÉTÉ ?
 *
 * `affretement_id` et lui seul. `affrete_par` peut être présent sans que
 * l'ordre le soit — non, il ne le peut pas : l'API les rend par la même
 * jointure, sur l'affrètement NON ANNULÉ
 * (`apps/api/src/ordres_transport.ts`, `left join affretements af on
 * af.ot_id = o.id and af.annule_le is null`). Mais c'est l'identifiant qui
 * porte le fait ; le nom n'est qu'une commodité d'affichage, et un nom vide ne
 * doit pas faire disparaître la pastille.
 */
export function estAffrete(ordre: {
  readonly affretement_id: string | null;
}): boolean {
  return ordre.affretement_id !== null;
}

/**
 * L'INFOBULLE de la pastille : « Affrété par DUMONT · achat 1 940,00 € ».
 *
 * Rend `null` si l'ordre n'est pas affrété — il n'y a alors pas de pastille à
 * survoler. Le nom manquant ne fait pas mentir la phrase : on écrit « Affrété »
 * tout court plutôt que « Affrété par null ».
 *
 * Le prix d'achat y figure parce que c'est la question suivante — combien —, et
 * qu'il n'a pas sa place dans une colonne du tableau : la vente y est déjà, les
 * deux côte à côte inviteraient à lire une marge que la liste ne calcule pas.
 */
export function affreteDit(ordre: {
  readonly affretement_id: string | null;
  readonly affrete_par: string | null;
  readonly prix_achat: string | null;
}): string | null {
  if (!estAffrete(ordre)) return null;
  const qui =
    ordre.affrete_par === null || ordre.affrete_par.trim() === ''
      ? 'Affrété'
      : `Affrété par ${ordre.affrete_par}`;
  const combien = enEuros(ordre.prix_achat);
  return combien === null ? qui : `${qui} · achat ${combien}`;
}
